import React, { FC, useMemo } from "react";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useGetSessionsQuery } from "@store/sessionApi";
import { Order } from "@models/page-options";
import ConversationItem from "@components/Home/RecentConversationsTile/ConversationItem";
import { Box, Skeleton, Typography } from "@mui/material";
import { Session } from "@models/session";
import { DateUtil } from "@utils/date.util";
import StyledLink from '@components/StyledLink/StyledLink';
import EmptyState from '@components/EmptyState/EmptyState';

const GhostLoader: FC = () => {
  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {Array.from(Array(3)).map((_, index) => (
        <Skeleton key={index} variant="rectangular" height="115px" />
      ))}
    </Box>
  );
};

interface SessionGroup {
  date: number;
  sessions: Session[];
}

const groupSessions = (sessions: Session[]): SessionGroup[] => {
  return sessions.reduce<SessionGroup[]>((acc, curr) => {
    const group = acc.find(
      ({ date }) =>
        (DateUtil.startOfDay(curr.createdDate) as Date).getTime() === date,
    );
    if (group) {
      group.sessions.push(curr);
    } else {
      acc.push({
        date: (DateUtil.startOfDay(curr.createdDate) as Date).getTime(),
        sessions: [curr],
      });
    }
    return acc;
  }, []);
};

const RecentConversationsTile: FC = () => {
  const { t } = useTranslation();
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );

  const { data: sessions, isLoading } = useGetSessionsQuery(
    { patientId: patientId!, order: Order.DESC, page: 1, pageSize: 3 },
    {
      skip: !patientId,
    },
  );

  const sessionGroups: SessionGroup[] = useMemo(() => {
    if (!sessions?.data) {
      return [];
    }
    return groupSessions(sessions.data);
  }, [sessions]);

  return (
    <DashboardTile title={t("dashboard.recentConversations.title")}>
      {!sessionGroups || isLoading ? (
        <GhostLoader />
      ) : (
        <Box display="flex" flexDirection="column" gap="16px">
          {sessionGroups?.length ? (
            sessionGroups.map(({ date, sessions }) => {
              const daysAgo = DateUtil.getDaysAgo(date);
              return (
                <Box key={date}>
                  <Typography
                    fontSize="14px"
                    lineHeight="22px"
                    fontWeight="400"
                    color="#171A1FFF"
                    marginBottom="6px"
                  >
                    {t(daysAgo ? "daysAgo" : "today", { days: daysAgo })}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap="16px">
                    {sessions.map((session) => (
                      <ConversationItem key={session.id} session={session} />
                    ))}
                  </Box>
                </Box>
              );
            })
          ) : (
            <EmptyState image={`${process.env.PUBLIC_URL}/images/art-conversation.jpeg`} label={t("dashboard.recentConversations.noData")}/>
          )}
          {sessionGroups?.length ?
            <StyledLink to='/transcripts/'>
              {t('common.viewAll')}
            </StyledLink> : <></>
          }
        </Box>
      )}
    </DashboardTile>
  );
};

export default RecentConversationsTile;
